//@ts-check
import { deviceToken } from "$lib/native.js";

export const sleep = (m) => new Promise((r) => setTimeout(r, m));

/** @type {(() => void) | null} */
let unauthorizedHandler = null;

/**
 * What to do when the API stops accepting this session while it is in use: a device revoked from
 * the devices list, a token or cookie that has run out. Set by the signed-in layout, and null
 * everywhere else, so a 401 on the sign-in page (an expired sign-in attempt) is left to that page.
 *
 * @param {(() => void) | null} fn
 */
export function onUnauthorized(fn) {
  unauthorizedHandler = fn;
}

export async function getfetchopts(opts = {}) {
  const kyopts = {
    timeout: 30000,
    hooks: {
      // ky 2 hands every hook one state object. Written against ky 1's positional arguments,
      // `request`/`response` arrive as that object instead: the log prints "undefined undefined",
      // `error.response` is never seen so 401s are never classified, and reading `response.headers`
      // throws before any request can complete.
      beforeRequest: [
        ({ request }) => {
          console.info(`Request: ${request.method} ${request.url}`);
          if (kyopts.json && !opts.noconsole) {
            console.info(JSON.stringify(kyopts.json, null, 2));
          }
        },
      ],
      beforeError: [
        async ({ error }) => {
          if (error.response) {
            if (error.response.status == 401) {
              error.isautherr = true;
              // The identity API's 401s are answers in their own right: `/me`'s is "signed out",
              // which `loadSession` handles, and logout's no longer matters. Any other is a session
              // refused mid-use, wherever it happens: a capture, a list, a refresh on resume.
              if (unauthorizedHandler && !new URL(error.request.url).pathname.startsWith("/api/auth/")) {
                unauthorizedHandler();
              }
            }
            if (error.response.status == 403) {
              error.isauthzerr = true;
            }
            // ky 2 has already read the body into `error.data` (parsed when it is JSON) before this
            // hook runs, so `error.response.json()` would fail on a consumed body.
            const responsebody = error.data;
            if (responsebody && typeof responsebody === "object") {
              error.body = responsebody;
              if (responsebody.message) {
                error.message = responsebody.message;
              }
            }
          }
          return error;
        },
      ],
      afterResponse: [
        ({ response }) => {
          // CloudFront's SPA error pages (403/404 → /200.html, status 200) are distribution-wide, so
          // an API 403/404 comes back as 200 + the HTML app shell. Without this, callers do `.json()`
          // on HTML and get a cryptic "Unexpected token '<'". Detect that masked response and turn it
          // into a real, classified error (most often an expired session or missing permission).
          const ct = response.headers.get("content-type") || "";
          if (response.ok && ct.includes("text/html")) {
            const err = /** @type {any} */ (
              new Error(
                "Your session may have expired or you don't have permission for this action. Please sign in again."
              )
            );
            // Mirror the auth flags set in beforeError so callers can branch on it.
            err.isauthzerr = true;
            err.maskedError = true;
            throw err;
          }
        },
      ],
    },
    retry: 0,
  };

  if (opts) {
    Object.assign(kyopts, opts);
  }

  // Inside the iOS app there is no cookie: the device token goes in the header instead
  // ($lib/native.js). Always null on the web, where the session cookie does the work.
  const token = await deviceToken();
  if (token) {
    kyopts.headers = { ...kyopts.headers, authorization: `Bearer ${token}` };
  }

  return kyopts;
}
